import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Apple,
  ArrowRight,
  BookOpen,
  BrainCircuit,
  Check,
  CheckCircle2,
  Code2,
  Command,
  Copy,
  Database,
  Download,
  FileSearch,
  Globe2,
  HardDrive,
  Layers3,
  Monitor,
  Network,
  Server,
  ShieldCheck,
  Sparkles,
  Terminal,
  Workflow,
} from 'lucide-react';
import SEO from '@/components/common/SEO';
import Header from '@/components/layout/Header';
import MainLayout from '@/components/layout/MainLayout';
import CorporateFooter from '@/components/layout/CorporateFooter';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { buildFaqSchema } from '@/lib/seo';
import { cn } from '@/lib/utils';

type PlatformId = 'windows' | 'macos' | 'linux';
type Arch = 'arm64' | 'x64';
type DemoId = 'question' | 'files' | 'json' | 'server';

type PlatformDownload = {
  id: PlatformId;
  label: string;
  icon: React.ElementType;
  url: string;
  fallbackFileName: string;
  command: string;
};

const RESEARCH_MANIFEST_URL = 'https://gateway.canxp.ai/downloads/research/latest.json';
const RESEARCH_DOWNLOAD_BASE = 'https://gateway.canxp.ai/downloads/research';

const platformManifestKey = (id: PlatformId, arch: Arch): string =>
  id === 'windows' ? 'win32-x64' : `${id === 'macos' ? 'darwin' : 'linux'}-${arch}`;

const platformDownloads: PlatformDownload[] = [
  {
    id: 'windows',
    label: 'Windows',
    icon: Monitor,
    url: `${RESEARCH_DOWNLOAD_BASE}/CanXP-Research-Setup-1.9.0.exe`,
    fallbackFileName: 'CanXP-Research-Setup.exe',
    command: 'canxp-research --version',
  },
  {
    id: 'macos',
    label: 'macOS',
    icon: Apple,
    url: `${RESEARCH_DOWNLOAD_BASE}/CanXP-Research-1.9.0-arm64.pkg`,
    fallbackFileName: 'CanXP-Research.pkg',
    command: 'canxp-research --version',
  },
  {
    id: 'linux',
    label: 'Linux',
    icon: Terminal,
    url: `${RESEARCH_DOWNLOAD_BASE}/canxp-research-1.9.0-linux-x64.tar.gz`,
    fallbackFileName: 'canxp-research.tar.gz',
    command: 'curl -fsSL https://canxp.ai/install | bash',
  },
];

const defaultArch: Record<PlatformId, Arch> = { windows: 'x64', macos: 'arm64', linux: 'x64' };
const archLabels: Record<PlatformId, Record<Arch, string>> = {
  windows: { x64: 'Windows 10/11 · x64', arm64: 'Windows 10/11 · x64' },
  macos: { arm64: 'Apple silicon · macOS 12+', x64: 'Intel Mac · macOS 12+' },
  linux: { x64: 'Linux x64 · glibc', arm64: 'Linux arm64 · glibc' },
};

const fields = [
  { name: 'medicine', skills: 23, cost: '~2,800', description: 'Evidence-based clinical reasoning' },
  { name: 'genomics', skills: 35, cost: '~3,500', description: 'Reference-aware genomic analysis' },
  { name: 'data-science', skills: 28, cost: '~2,700', description: 'Estimands, assumptions, and leakage checks' },
  { name: 'phd', skills: 38, cost: '~4,600', description: 'Study design and scholarly writing' },
  { name: 'engineering', skills: 17, cost: '~1,400', description: 'First principles, standards, and tolerances' },
  { name: 'software', skills: 10, cost: '~1,200', description: 'Correct, tested, project-native code' },
  { name: 'finance', skills: 67, cost: '~2,400', description: 'Bias-aware quantitative research' },
  { name: 'chemistry', skills: 12, cost: '~960', description: 'Structure-precise chemical reasoning' },
  { name: 'legal', skills: 0, cost: '0', description: 'Jurisdiction-aware IRAC reasoning' },
  { name: 'security', skills: 0, cost: '0', description: 'Threats, controls, and risk weighting' },
  { name: 'cybersecurity', skills: 0, cost: '0', description: 'CWE, CVE, ATT&CK, and defense' },
  { name: 'general', skills: 0, cost: '0', description: 'The base research assistant' },
  { name: 'all', skills: 230, cost: '~19,700', description: 'Every skill, without a field persona' },
];

const modes = [
  {
    name: 'code',
    icon: Code2,
    label: 'Default',
    description: 'Read and write files, run commands, search, and delegate. The full-capability research and coding agent.',
  },
  {
    name: 'ask',
    icon: BookOpen,
    label: 'Read-only answers',
    description: 'Explain, inspect, and search without changing your project. For answers rather than diffs.',
  },
  {
    name: 'plan',
    icon: Workflow,
    label: 'Edits denied',
    description: 'Explore and design an approach while edit tools are denied at the permission boundary.',
  },
  {
    name: 'debug',
    icon: FileSearch,
    label: 'Diagnostic',
    description: 'Reproduce, isolate, form hypotheses, and test them instead of guessing at a fix.',
  },
  {
    name: 'orchestrator',
    icon: Network,
    label: 'Parallel work',
    description: 'Break large research programs into independent units, delegate them, and synthesize the result.',
  },
];

const demos: Record<DemoId, { label: string; command: string; lines: Array<{ prefix?: string; text: string; tone?: string }> }> = {
  question: {
    label: 'Research question',
    command: 'canxp-research run "tell me about frogs"',
    lines: [
      { prefix: '●', text: ' Starting session', tone: 'text-emerald-400' },
      { prefix: '●', text: ' Field: general  ·  Agent: code', tone: 'text-emerald-400' },
      { text: '' },
      { text: 'Frogs are tailless amphibians in the order Anura, with more than 7,000' },
      { text: 'known species. They are ecological indicators because their permeable skin and' },
      { text: 'dual aquatic–terrestrial life cycle make them sensitive to environmental change.' },
      { text: '' },
      { prefix: '✓', text: ' Complete  ·  session ses_7fa2…', tone: 'text-emerald-400' },
    ],
  },
  files: {
    label: 'Files + evidence',
    command: 'canxp-research run -f data.csv -f protocol.pdf "Do the numbers in the PDF match the CSV?"',
    lines: [
      { prefix: '●', text: ' Reading data.csv', tone: 'text-sky-400' },
      { prefix: '●', text: ' Reading protocol.pdf', tone: 'text-sky-400' },
      { prefix: '●', text: ' Running statistical checks', tone: 'text-sky-400' },
      { text: '' },
      { text: 'Two reported values do not match the source data:' },
      { text: '  • Table 2 reports n=184; the filtered CSV contains n=181.' },
      { text: '  • The reported median is 12.4; recalculation gives 11.9.' },
      { text: '' },
      { prefix: '✓', text: ' Sources and calculations included', tone: 'text-emerald-400' },
    ],
  },
  json: {
    label: 'JSON pipeline',
    command: 'canxp-research run --format json --agent ask "In one sentence, what is a p-value?"',
    lines: [
      { text: '{"type":"step_start","sessionID":"ses_fa7e…","part":{…}}', tone: 'text-zinc-400' },
      { text: '{"type":"text","sessionID":"ses_fa7e…","part":{"type":"text",', tone: 'text-amber-200' },
      { text: ' "text":"A p-value is the probability of obtaining results as extreme', tone: 'text-amber-200' },
      { text: ' as those observed, assuming the null hypothesis is true."}}', tone: 'text-amber-200' },
      { text: '{"type":"step_finish","sessionID":"ses_fa7e…","part":{…}}', tone: 'text-zinc-400' },
    ],
  },
  server: {
    label: 'Headless server',
    command: 'canxp-research serve --port 4399',
    lines: [
      { prefix: '●', text: ' CanXP Research server', tone: 'text-emerald-400' },
      { text: '  Local:    http://127.0.0.1:4399' },
      { text: '  OpenAPI:  http://127.0.0.1:4399/doc' },
      { text: '' },
      { text: '  Routes: config · session · provider · file · project' },
      { text: '          permission · event · mcp · query · workspace' },
      { text: '' },
      { prefix: '✓', text: ' Ready for clients and canxp-research attach', tone: 'text-emerald-400' },
    ],
  },
};

const capabilityCards: {
  icon: React.ComponentType<{ className?: string }>;
  number: string;
  title: string;
  description: string;
  command: string;
  href?: string;
  linkLabel?: string;
}[] = [
  {
    icon: FileSearch,
    number: '01',
    title: 'Search every source together',
    description: 'Fuse results from workspace files, a local vector index, Obsidian, and approved MCP tools into one ranked answer with provenance on every hit.',
    command: 'research_search → files + index + vault + MCP',
  },
  {
    icon: Database,
    number: '02',
    title: 'Connect live data through MCP',
    description: 'Reach an EHR, warehouse, internal API, or vault without placing credentials in the model context. Sources remain explicit and permission-aware.',
    command: 'canxp-research mcp add',
    href: '/mcp',
    linkLabel: 'How to connect your data',
  },
  {
    icon: BrainCircuit,
    number: '03',
    title: 'Load expertise on demand',
    description: 'Skills add domain-specific methods only when relevant. Your selected field carries the right expertise without spending context on all 230 skills.',
    command: '/field  ·  /skills',
  },
  {
    icon: Server,
    number: '04',
    title: 'Run anywhere',
    description: 'Use the full-screen TUI, a non-interactive command, a long-lived HTTP server, browser interface, daemon, or an ACP-capable editor.',
    command: 'run  ·  serve  ·  web  ·  acp  ·  daemon',
  },
];

const commandReference = [
  ['canxp-research', 'Open the terminal interface'],
  ['canxp-research run "…"', 'Run once without the TUI'],
  ['canxp-research run -c "…"', 'Continue the latest session'],
  ['canxp-research run -s <id> "…"', 'Continue a specific session'],
  ['canxp-research run --agent plan "…"', 'Plan with edits denied'],
  ['canxp-research run -m provider/model "…"', 'Choose a model for one run'],
  ['canxp-research serve --port 4399', 'Start the HTTP API'],
  ['canxp-research acp --cwd <path>', 'Start an ACP agent backend'],
  ['canxp-research mcp add', 'Connect your own data'],
  ['canxp-research config check', 'Validate effective configuration'],
];

const slashCommands = ['/field', '/agents', '/models', '/skills', '/mcps', '/sessions', '/new', '/status', '/editor', '/warp'];

const researchFaqs = [
  {
    question: 'What is CanXP Research?',
    answer: 'CanXP Research is a terminal-native research agent distributed as a single binary. It reads and writes files, runs commands, searches the web, connects to your data through MCP, and loads domain skills on demand. It is a research assistant first and a coding assistant second.',
  },
  {
    question: 'How do I run a question from a terminal?',
    answer: 'Use canxp-research run followed by your prompt in quotes. For example: canxp-research run "tell me about frogs". Add --format json for a newline-delimited event stream that another tool or script can parse.',
  },
  {
    question: 'Does it require Node, Python, or another runtime?',
    answer: 'No. CanXP Research is installed as a binary on Windows, macOS, and Linux. The Linux install script and platform installers put canxp-research on your PATH.',
  },
  {
    question: 'How do research fields work?',
    answer: 'A field adds a specialist persona to every mode and makes a curated set of domain skills available. Set it with /field or the field key in canxp.jsonc. The setting takes effect in your next session.',
  },
  {
    question: 'Can I safely ask for a plan without changing files?',
    answer: 'Yes. Plan mode denies edit tools at the permission boundary. Ask mode is also appropriate when you want an explanation or investigation rather than a diff.',
  },
  {
    question: 'Can another AI tool call CanXP Research?',
    answer: 'Yes. Any tool that can execute a command can call canxp-research run --format json and read its event stream. For longer-lived integrations, use the HTTP server or ACP mode.',
  },
  {
    question: 'Where are configuration and sessions stored?',
    answer: 'Configuration lives in ~/.config/canxp, session data and credentials in ~/.local/share/canxp, and downloaded skills and snapshots in ~/.cache/canxp. XDG base directory variables are respected.',
  },
];

const detectPlatform = (): PlatformId => {
  if (typeof window === 'undefined') return 'windows';
  const value = `${window.navigator.userAgent} ${window.navigator.platform}`.toLowerCase();
  if (value.includes('mac')) return 'macos';
  if (value.includes('linux')) return 'linux';
  return 'windows';
};

const detectArch = async (): Promise<Arch> => {
  if (typeof window === 'undefined') return 'x64';
  type UAData = { getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }> };
  const uaData = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
  if (uaData?.getHighEntropyValues) {
    try {
      const { architecture } = await uaData.getHighEntropyValues(['architecture']);
      if (architecture === 'arm') return 'arm64';
      if (architecture === 'x86') return 'x64';
    } catch {
      // Fall back to the user agent and platform defaults.
    }
  }
  const value = navigator.userAgent.toLowerCase();
  if (value.includes('arm64') || value.includes('aarch64')) return 'arm64';
  return value.includes('mac') ? 'arm64' : 'x64';
};

const SectionHeading = ({ eyebrow, title, description, align = 'center' }: { eyebrow: string; title: string; description: string; align?: 'left' | 'center' }) => (
  <div className={cn('max-w-3xl', align === 'center' && 'mx-auto text-center')}>
    <p className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-primary">{eyebrow}</p>
    <h2 className="text-3xl font-semibold tracking-[-0.035em] text-foreground sm:text-4xl lg:text-5xl">{title}</h2>
    <p className="mt-5 text-base leading-8 text-muted-foreground sm:text-lg">{description}</p>
  </div>
);

const CopyCommand = ({ command, inverse = false }: { command: string; inverse?: boolean }) => {
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy ${command}`}
      className={cn(
        'inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors',
        inverse ? 'bg-white/10 text-zinc-300 hover:bg-white/15 hover:text-white' : 'bg-muted text-muted-foreground hover:text-foreground',
      )}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const TerminalDemo = () => {
  const [active, setActive] = React.useState<DemoId>('question');
  const demo = demos[active];

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0b0d10] shadow-2xl shadow-black/40">
      <div className="flex items-center justify-between border-b border-white/10 bg-[#12151a] px-4 py-3">
        <div className="flex gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        </div>
        <span className="font-mono text-[11px] text-zinc-500">canxp-research</span>
        <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,.8)]" />
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-white/10 bg-[#0e1115] p-2">
        {(Object.entries(demos) as [DemoId, (typeof demos)[DemoId]][]).map(([id, item]) => (
          <button
            key={id}
            type="button"
            onClick={() => setActive(id)}
            className={cn(
              'whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              active === id ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="min-h-[340px] p-5 font-mono text-[12px] leading-6 sm:p-6 sm:text-[13px]">
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <span className="select-none font-bold text-primary">❯</span>
          <code className="min-w-0 flex-1 break-words text-zinc-100">{demo.command}</code>
          <CopyCommand command={demo.command} inverse />
        </div>
        <div className="overflow-x-auto text-zinc-300" aria-live="polite">
          {demo.lines.map((line, index) => (
            <div key={`${active}-${index}`} className={cn('min-h-6 whitespace-pre-wrap', line.tone)}>
              {line.prefix ? <span className={line.tone}>{line.prefix}</span> : null}{line.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ResearchPage = () => {
  const [selectedPlatform, setSelectedPlatform] = React.useState<PlatformId>('windows');
  const [detectedPlatform, setDetectedPlatform] = React.useState<PlatformId>('windows');
  const [arch, setArch] = React.useState<Arch>('x64');
  const [version, setVersion] = React.useState<string | null>(null);
  const [manifest, setManifest] = React.useState<Record<string, string> | null>(null);
  const downloadsRef = React.useRef<HTMLElement | null>(null);
  const [downloadUrls, setDownloadUrls] = React.useState<Record<PlatformId, string>>(() =>
    platformDownloads.reduce((urls, item) => ({ ...urls, [item.id]: item.url }), {} as Record<PlatformId, string>),
  );

  React.useEffect(() => {
    const platform = detectPlatform();
    setDetectedPlatform(platform);
    setSelectedPlatform(platform);
    let active = true;
    void detectArch().then((result) => active && setArch(result));
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    let active = true;
    fetch(RESEARCH_MANIFEST_URL, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { version?: string; downloads?: Record<string, string> } | null) => {
        if (!active || !data) return;
        if (data.version) setVersion(data.version);
        setManifest(data.downloads ?? {});
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (!manifest) return;
    setDownloadUrls((current) => {
      const next = { ...current };
      platformDownloads.forEach((platform) => {
        const targetArch = platform.id === detectedPlatform ? arch : defaultArch[platform.id];
        const url = platform.id === 'macos'
          ? version && `${RESEARCH_DOWNLOAD_BASE}/CanXP-Research-${version}-${targetArch}.pkg`
          : manifest[platformManifestKey(platform.id, targetArch)];
        if (url) next[platform.id] = url;
      });
      return next;
    });
  }, [arch, detectedPlatform, manifest, version]);

  const archFor = (id: PlatformId) => (id === detectedPlatform ? arch : defaultArch[id]);
  const selectedDownload = platformDownloads.find((item) => item.id === selectedPlatform) ?? platformDownloads[0];
  const fileNameFor = (platform: PlatformDownload) => downloadUrls[platform.id]?.split('/').pop() || platform.fallbackFileName;
  const scrollToDownloads = () => downloadsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <MainLayout>
      <SEO
        title="CanXP Research — Terminal-native research agent"
        description="CanXP Research reads files, runs commands, searches the web, connects your data through MCP, and loads specialist research skills from one cross-platform binary."
        keywords="CanXP Research, research agent, AI CLI, MCP client, terminal research assistant, research skills, headless AI server, ACP"
        url="/research"
        schema={buildFaqSchema(researchFaqs)}
      />

      <div className="min-h-screen overflow-hidden bg-background text-foreground">
        <Header />
        <main>
          <section className="relative isolate overflow-hidden border-b border-white/10 bg-[#090b0f] text-white">
            <div className="absolute inset-0 -z-20 bg-[linear-gradient(115deg,#090b0f_0%,#10141a_55%,#180d10_100%)]" />
            <div className="absolute left-[-12%] top-[-25%] -z-10 h-[620px] w-[620px] rounded-full bg-primary/20 blur-[140px]" />
            <div className="absolute bottom-[-45%] right-[-10%] -z-10 h-[620px] w-[620px] rounded-full bg-sky-500/10 blur-[160px]" />
            <div className="absolute inset-0 -z-10 opacity-20 [background-image:linear-gradient(rgba(255,255,255,.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.05)_1px,transparent_1px)] [background-size:52px_52px] [mask-image:linear-gradient(to_bottom,black,transparent_85%)]" />

            <div className="container mx-auto grid gap-14 px-4 py-20 sm:py-28 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:py-32">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
                <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-medium text-zinc-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  One binary. For every serious research project.
                </div>
                <h1 className="max-w-3xl text-5xl font-semibold leading-[0.96] tracking-[-0.055em] sm:text-6xl xl:text-7xl">
                  Research    
                  <span className="block bg-gradient-to-r from-primary via-red-400 to-orange-300 bg-clip-text text-transparent">beyond the answers.</span>
                </h1>
                <p className="mt-7 max-w-2xl text-lg leading-8 text-zinc-300 sm:text-xl">
                  CanXP Research is a terminal-native agent that reads and writes files, runs commands, searches the web, connects to your data, and loads expertise for your field. Over 230+ domain skills.
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <Button size="lg" className="h-12 rounded-lg px-6 text-base" onClick={scrollToDownloads}>
                    <Download className="mr-2 h-4 w-4" />
                    Download for {selectedDownload.label}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 rounded-lg border-white/15 bg-white/[0.04] px-6 text-base text-white hover:bg-white/10 hover:text-white"
                    onClick={() => document.getElementById('capabilities')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Explore capabilities <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
                  {['Windows, macOS, Linux', 'No runtime required', 'TUI, CLI, HTTP, ACP'].map((item) => (
                    <span key={item} className="inline-flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> {item}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.65, delay: 0.08 }}>
                <TerminalDemo />
              </motion.div>
            </div>
          </section>

          <section className="border-b bg-muted/30">
            <div className="container mx-auto grid grid-cols-2 divide-x divide-y border-x sm:grid-cols-4 sm:divide-y-0">
              {[
                ['230', 'domain skills'],
                ['13', 'field options'],
                ['5', 'primary agents'],
                ['6', 'ways to run'],
              ].map(([value, label]) => (
                <div key={label} className="px-5 py-7 text-center">
                  <div className="text-3xl font-semibold tracking-tight">{value}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
          </section>

          <section id="capabilities" className="py-20 sm:py-28">
            <div className="container mx-auto px-4">
              <div className="grid gap-10 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
                <SectionHeading
                  eyebrow="Research, not just chat"
                  title="One agent across the whole evidence trail."
                  description="The same tool can audit a manuscript, run an analysis, search your notes, query a live system, and update the code that makes it reproducible. Built for research across medicine, genomics, chemistry, data science, academia, engineering, software, finance, law, security, and cybersecurity."
                  align="left"
                />
                <div className="grid gap-px overflow-hidden rounded-2xl border bg-border md:grid-cols-2">
                  {capabilityCards.map((item) => {
                    const Icon = item.icon;
                    return (
                      <article key={item.title} className="group bg-background p-6 transition-colors hover:bg-muted/40 sm:p-7">
                        <div className="flex items-center justify-between">
                          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <Icon className="h-5 w-5" />
                          </div>
                          <span className="font-mono text-xs text-muted-foreground">{item.number}</span>
                        </div>
                        <h3 className="mt-6 text-xl font-semibold tracking-tight">{item.title}</h3>
                        <p className="mt-3 text-sm leading-7 text-muted-foreground">{item.description}</p>
                        <code className="mt-5 block overflow-x-auto rounded-lg bg-muted px-3 py-2 text-xs text-foreground">{item.command}</code>
                        {item.href && (
                          <Link
                            to={item.href}
                            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                          >
                            {item.linkLabel ?? 'Learn more'} <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        )}
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          <section className="border-y bg-[#0c0f13] py-20 text-white sm:py-28">
            <div className="container mx-auto px-4">
              <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
                <div className="lg:sticky lg:top-28">
                  <p className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-primary">Field-aware reasoning</p>
                  <h2 className="text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">It reasons like your field demands.</h2>
                  <p className="mt-5 max-w-xl text-lg leading-8 text-zinc-400">
                    A field changes the agent’s specialist persona and the skills available in every session. Carry the expertise you use—not all 230 skill descriptions.
                  </p>
                  <div className="mt-7 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="flex items-center gap-3 font-mono text-sm">
                      <span className="text-primary">❯</span><span className="text-zinc-200">/field</span>
                    </div>
                    <div className="mt-3 text-xs leading-6 text-zinc-500">Or set once in config: <code className="text-zinc-300">{'{ "field": "medicine" }'}</code></div>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {fields.map((field) => (
                    <div key={field.name} className="rounded-xl border border-white/10 bg-white/[0.035] p-4 transition-colors hover:border-white/20 hover:bg-white/[0.06]">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-mono text-sm font-semibold text-white">{field.name}</h3>
                          <p className="mt-2 text-xs leading-5 text-zinc-400">{field.description}</p>
                        </div>
                        <span className="rounded-md bg-white/[0.06] px-2 py-1 font-mono text-[10px] text-zinc-400">{field.skills} skills</span>
                      </div>
                      <div className="mt-3 text-[10px] uppercase tracking-wider text-zinc-600">Context {field.cost} tokens</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 sm:py-28">
            <div className="container mx-auto px-4">
              <SectionHeading
                eyebrow="Agents with boundaries"
                title="Choose a mode. Get enforceable behavior."
                description="Each mode has its own system prompt and permission set. Use Plan mode to plan without disrupting your work."
              />
              <div className="mt-12 grid gap-4 lg:grid-cols-5">
                {modes.map((mode, index) => {
                  const Icon = mode.icon;
                  return (
                    <Card key={mode.name} className={cn('relative overflow-hidden bg-card/60', index === 0 && 'border-primary/50')}>
                      {index === 0 ? <div className="absolute inset-x-0 top-0 h-0.5 bg-primary" /> : null}
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <Icon className="h-5 w-5 text-primary" />
                          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{mode.label}</span>
                        </div>
                        <CardTitle className="pt-5 font-mono text-lg">{mode.name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm leading-6 text-muted-foreground">{mode.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="mt-5 flex flex-col items-start justify-between gap-4 rounded-xl border bg-muted/30 p-5 sm:flex-row sm:items-center">
                <div>
                  <p className="font-semibold">Need your own agent?</p>
                  <p className="mt-1 text-sm text-muted-foreground">Agents are portable Markdown files with YAML frontmatter in <code>.canxp/agent/</code>.</p>
                </div>
                <code className="rounded-lg bg-background px-4 py-2 text-xs">canxp-research agent create</code>
              </div>
            </div>
          </section>

          <section className="border-y bg-muted/30 py-20 sm:py-28">
            <div className="container mx-auto px-4">
              <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
                <div>
                  <SectionHeading
                    eyebrow="Your data, with provenance"
                    title="Search everything. Know what answered."
                    description="Research Search asks configured sources in parallel, merges their rankings, and labels every result with its origin. Empty and failed are never treated as the same thing."
                    align="left"
                  />
                  <div className="mt-8 space-y-3">
                    {[
                      ['index', 'Semantic search over the local vector index'],
                      ['files', 'Literal workspace search with ripgrep'],
                      ['vault', 'Full-text search across an Obsidian vault'],
                      ['mcp', 'An approved EHR, warehouse, or internal API tool'],
                    ].map(([name, description]) => (
                      <div key={name} className="flex items-center gap-4 rounded-xl border bg-background p-4">
                        <span className="w-14 font-mono text-xs font-bold text-primary">{name}</span>
                        <span className="text-sm text-muted-foreground">{description}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0b0d10] p-5 font-mono text-xs leading-6 text-zinc-300 shadow-2xl sm:p-7 sm:text-sm">
                  <p className="text-zinc-500">Where do we handle patient consent?</p>
                  <p className="mt-5 text-zinc-100">3 documents, ranked across 2 sources.</p>
                  <div className="mt-4 border-l-2 border-primary pl-4">
                    <p className="text-white">1. src/consent.ts <span className="text-primary">[files#1, index#2]</span></p>
                    <p className="mt-1 text-zinc-500">14–22: export function recordConsent(…)</p>
                  </div>
                  <div className="mt-4 border-l-2 border-white/10 pl-4">
                    <p className="text-white">2. docs/consent-policy.md <span className="text-primary">[index#1]</span></p>
                    <p className="mt-1 text-zinc-500">2–6: Consent must be re-captured…</p>
                  </div>
                  <div className="mt-6 border-t border-white/10 pt-4 text-zinc-500">
                    <p>Sources:</p>
                    <p><span className="text-emerald-400">index: ok (4)</span> · <span className="text-emerald-400">files: ok (11)</span></p>
                    <p><span className="text-zinc-400">vault: empty</span> · <span className="text-amber-400">mcp: excluded</span></p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 sm:py-28">
            <div className="container mx-auto px-4">
              <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
                <div>
                  <SectionHeading
                    eyebrow="Command reference"
                    title="One binary. Six interfaces."
                    description="Work interactively, automate a single run, expose an API, open the web client, speak ACP, or keep a daemon warm in the background."
                    align="left"
                  />
                  <div className="mt-8 flex flex-wrap gap-2">
                    {slashCommands.map((command) => (
                      <Badge key={command} variant="outline" className="rounded-md bg-background px-3 py-1.5 font-mono font-normal">{command}</Badge>
                    ))}
                  </div>
                </div>
                <div className="overflow-hidden rounded-2xl border bg-background">
                  {commandReference.map(([command, description], index) => (
                    <div key={command} className={cn('grid gap-3 p-4 sm:grid-cols-[1.2fr_0.8fr] sm:items-center sm:px-5', index !== 0 && 'border-t')}>
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="font-mono text-xs text-primary">$</span>
                        <code className="min-w-0 break-all text-xs text-foreground sm:text-sm">{command}</code>
                      </div>
                      <p className="text-sm text-muted-foreground sm:text-right">{description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section ref={downloadsRef} id="downloads" className="scroll-mt-20 border-y bg-[#0c0f13] py-20 text-white sm:py-28">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-3xl text-center">
                <p className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-primary">Install CanXP Research</p>
                <h2 className="text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">A native binary for your OS.</h2>
                <p className="mt-5 text-lg leading-8 text-zinc-400">Install, open a new terminal, and type <code className="text-zinc-200">canxp-research</code>. Sign in with your CanXP account when prompted.</p>
              </div>

              <div className="mt-12 grid gap-4 lg:grid-cols-3">
                {platformDownloads.map((platform) => {
                  const Icon = platform.icon;
                  const selected = selectedPlatform === platform.id;
                  return (
                    <article
                      key={platform.id}
                      className={cn(
                        'relative overflow-hidden rounded-2xl border bg-white/[0.035] p-6 transition-all',
                        selected ? 'border-primary/70 shadow-[0_0_0_1px_rgba(220,38,38,.15)]' : 'border-white/10 hover:border-white/20',
                      )}
                    >
                      {selected ? <span className="absolute right-4 top-4 rounded-full bg-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">Detected</span> : null}
                      <Icon className="h-7 w-7 text-primary" />
                      <h3 className="mt-6 text-2xl font-semibold">{platform.label}</h3>
                      <p className="mt-2 text-sm text-zinc-500">{archLabels[platform.id][archFor(platform.id)]}</p>
                      <Button asChild variant={selected ? 'default' : 'outline'} className={cn('mt-7 h-auto w-full rounded-lg py-3', !selected && 'border-white/15 bg-white/[0.03] text-white hover:bg-white/10 hover:text-white')}>
                        <a href={downloadUrls[platform.id]} download onClick={() => setSelectedPlatform(platform.id)}>
                          <Download className="mr-2 h-4 w-4" />
                          <span className="min-w-0 text-left">
                            <span className="block">Download</span>
                            <span className="block break-all font-mono text-[10px] font-normal opacity-70">{fileNameFor(platform)}</span>
                          </span>
                        </a>
                      </Button>
                      <div className="mt-4 flex items-center gap-2 rounded-lg bg-black/30 p-2 pl-3 font-mono text-[11px] text-zinc-400">
                        <span className="min-w-0 flex-1 break-all">{platform.command}</span>
                        <CopyCommand command={platform.command} inverse />
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="mt-8 grid gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:grid-cols-3">
                {[
                  [ShieldCheck, 'Signed installers', 'macOS package is signed and notarized; Windows uses a signed installer.'],
                  [HardDrive, 'Predictable paths', 'Binary, config, sessions, and cache live in standard user directories.'],
                  [Sparkles, 'Updates in place', 'Run canxp-research upgrade, or let automatic updates use the release channel.'],
                ].map(([Icon, title, description]) => {
                  const ItemIcon = Icon as React.ElementType;
                  return (
                    <div key={title as string} className="flex gap-3 p-2">
                      <ItemIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <div>
                        <p className="text-sm font-semibold">{title as string}</p>
                        <p className="mt-1 text-xs leading-5 text-zinc-500">{description as string}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="py-20 sm:py-28">
            <div className="container mx-auto px-4">
              <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
                <div>
                  <SectionHeading
                    eyebrow="Designed for automation"
                    title="Your research specialist can work for other agents."
                    description="Claude Code, Codex, Cursor, scripts, and CI can invoke the binary and consume newline-delimited JSON. Your calling tool never needs your MCP credentials."
                    align="left"
                  />
                  <div className="mt-8 space-y-3">
                    {[
                      [Terminal, 'CLI', 'canxp-research run --format json'],
                      [Globe2, 'HTTP', 'canxp-research serve'],
                      [Layers3, 'ACP', 'canxp-research acp'],
                    ].map(([Icon, label, command]) => {
                      const ItemIcon = Icon as React.ElementType;
                      return (
                        <div key={label as string} className="flex items-center gap-4 rounded-xl border p-4">
                          <ItemIcon className="h-5 w-5 text-primary" />
                          <span className="w-10 text-xs font-bold uppercase text-muted-foreground">{label as string}</span>
                          <code className="text-xs text-foreground">{command as string}</code>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0b0d10] shadow-xl">
                  <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
                    <span className="font-mono text-xs text-zinc-500">ask-canxp.sh</span>
                    <CopyCommand inverse command={'canxp-research run --format json --agent ask "your question"'} />
                  </div>
                  <pre className="overflow-x-auto p-5 text-xs leading-6 text-zinc-300 sm:p-7 sm:text-sm"><code>{`#!/usr/bin/env bash
set -euo pipefail

QUESTION="\${1:?Pass a research question}"

canxp-research run --format json --agent ask "$QUESTION" \\
  | python3 -c '
import sys, json
out = []
for line in sys.stdin:
    try: event = json.loads(line)
    except ValueError: continue
    if event.get("type") == "text":
        out.append(event["part"]["text"])
print("".join(out).strip())
'`}</code></pre>
                </div>
              </div>
            </div>
          </section>

          <section className="border-y bg-muted/30 py-20 sm:py-28">
            <div className="container mx-auto max-w-4xl px-4">
              <SectionHeading
                eyebrow="Questions"
                title="Everything important before your first run."
                description="Installation, fields, permissions, automation, and where your local state lives."
              />
              <Accordion type="single" collapsible className="mt-10 rounded-2xl border bg-background px-5">
                {researchFaqs.map((faq, index) => (
                  <AccordionItem key={faq.question} value={`faq-${index}`}>
                    <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-7 text-muted-foreground sm:text-base">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          <section className="relative overflow-hidden bg-[#090b0f] px-4 py-20 text-white sm:py-28">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(220,38,38,.22),transparent_52%)]" />
            <div className="container relative mx-auto max-w-4xl text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <Command className="h-6 w-6" />
              </div>
              <h2 className="mt-7 text-4xl font-semibold tracking-[-0.045em] sm:text-6xl">Start with one command.</h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-zinc-400">Install CanXP Research, open your terminal, and give your work an agent that understands the discipline behind it.</p>
              <div className="mx-auto mt-8 flex max-w-2xl items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] p-3 pl-5 text-left font-mono text-sm">
                <span className="text-primary">❯</span>
                <code className="min-w-0 flex-1 break-words text-zinc-100">canxp-research run "tell me about frogs"</code>
                <CopyCommand inverse command={'canxp-research run "tell me about frogs"'} />
              </div>
              <Button size="lg" className="mt-7 h-12 rounded-lg px-7 text-base" onClick={scrollToDownloads}>
                <Download className="mr-2 h-4 w-4" /> Download for {selectedDownload.label}
              </Button>
            </div>
          </section>
        </main>
        <CorporateFooter />
      </div>
    </MainLayout>
  );
};

export default ResearchPage;
